// <img>/<video> for URLs behind /api auth. The browser cannot add auth headers to media
// requests, so the bytes are fetched with fetch() and shown through an object URL.

import { useEffect, useRef, useState, type ImgHTMLAttributes, type RefObject } from 'react';
import { errorText, fetchBlobUrl } from '../api.ts';

function useBlobUrl(url: string | null | undefined, enabled: boolean): { src: string | null; error: unknown } {
  const [state, setState] = useState<{ url: string | null; src: string | null; error: unknown }>({ url: null, src: null, error: null });
  useEffect(() => {
    if (!url || !enabled) return;
    let live = true;
    fetchBlobUrl(url).then(
      (src) => live && setState({ url, src, error: null }),
      (error: unknown) => live && setState({ url, src: null, error }),
    );
    return () => {
      live = false;
    };
  }, [url, enabled]);
  if (!url || state.url !== url) return { src: null, error: null };
  return { src: state.src, error: state.error };
}

function useVisible<T extends Element>(lazy: boolean): [RefObject<T | null>, boolean] {
  const ref = useRef<T | null>(null);
  const [visible, setVisible] = useState(!lazy);
  useEffect(() => {
    if (!lazy || visible) return;
    const el = ref.current;
    if (!el || typeof IntersectionObserver === 'undefined') {
      setVisible(true);
      return;
    }
    const io = new IntersectionObserver(
      (entries) => {
        if (entries.some((e) => e.isIntersecting)) {
          setVisible(true);
          io.disconnect();
        }
      },
      { rootMargin: '200px' },
    );
    io.observe(el);
    return () => io.disconnect();
  }, [lazy, visible]);
  return [ref, visible];
}

export function AuthImg({
  url,
  alt,
  lazy = false,
  className,
  fallback,
  ...rest
}: { url: string | null | undefined; alt: string; lazy?: boolean; className?: string; fallback?: string } & Omit<
  ImgHTMLAttributes<HTMLImageElement>,
  'src' | 'alt' | 'className'
>) {
  const [ref, visible] = useVisible<HTMLSpanElement>(lazy);
  const { src, error } = useBlobUrl(url, visible);
  if (!url) return <span className={`media-ph ${className ?? ''}`}>{fallback ?? 'No image'}</span>;
  return (
    <span ref={ref} className={`media-box ${className ?? ''}`}>
      {src ? (
        <img src={src} alt={alt} {...rest} />
      ) : error ? (
        <span className="media-ph err" title={errorText(error)}>
          Image unavailable
        </span>
      ) : (
        <span className="media-ph" aria-label="Loading image">
          …
        </span>
      )}
    </span>
  );
}

export function AuthVideo({ url, contentType, label }: { url: string; contentType: string; label: string }) {
  const { src, error } = useBlobUrl(url, true);
  if (error) return <div className="media-ph err">Clip unavailable: {errorText(error)}</div>;
  if (!src) return <div className="media-ph">Loading clip…</div>;
  return (
    <video className="clip" controls preload="metadata" aria-label={label}>
      <source src={src} type={contentType} />
    </video>
  );
}
